import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useRef, useState, forwardRef, useEffect } from "react";
import { ScrollSyncPane } from "react-scroll-sync";

import { camelCase, find } from "lodash";
import { flexRender } from "@tanstack/react-table";

import useOnWindowResize from "~/hooks/useOnWindowResize";
import useTableLayoutHelpers from "~/hooks/useTableLayoutHelpers";

import { TableWrap, StyledTable, Th, Tr, Td } from "~/components/core/Table";
import { IconWrapper, SortIcon } from "~/components/Table";

import { useProductImageContext } from "~/context/ProductImageContext";
import { useStickyContext } from "~/context/StickyContext";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { screenReaderText } from "~/styles/screenReaderText";

import { isBrowser } from "~/utilities/helpers";
import { verticalScrollListener } from "~/utilities/tableHelpers";

export const StyledTableWrap = styled(TableWrap)`
    margin-bottom: ${({ useStickyScrollbar, theme }) =>
        useStickyScrollbar
            ? `-${theme.table.scrollBarWidth}`
            : theme.layout.paragraphSpacing};

    &::-webkit-scrollbar-corner {
        background-color: ${({ theme, backgroundColor }) =>
            theme.sectionBackgroundColor[backgroundColor]};
    }
`;

const ProductTableTh = styled(Th)`
    cursor: ${({ canBeSorted }) => (canBeSorted ? "pointer" : "auto")};
    &:first-of-type {
        z-index: 2;
        left: ${({ firstColumnStaticOnMobile }) =>
            firstColumnStaticOnMobile ? "unset" : "0"};

        @media (min-width: ${({ theme }) =>
                theme.breakpoints.mobileLandscape}) {
            left: 0;
        }
    }

    &:nth-of-type(2) {
        @media (min-width: ${({ theme }) =>
                theme.breakpoints.mobileLandscape}) {
            z-index: 2;
            left: ${({ firstColWidth }) => `${firstColWidth}px`};
        }
    }
`;

const InnerTh = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 1.4rem;
`;

const ProductTableTd = styled(Td)`
    &:first-of-type {
        position: ${({ firstColumnStaticOnMobile }) =>
            firstColumnStaticOnMobile ? "static" : "sticky"};
        z-index: 1;
        left: 0;

        @media (max-width: ${({ theme }) =>
                theme.breakpoints.mobileLandscapeMax}) {
            border-right: ${({ theme, firstColumnStaticOnMobile }) =>
                firstColumnStaticOnMobile
                    ? ``
                    : `1px solid ${theme.palette.black}`};
        }

        @media (min-width: ${({ theme }) =>
                theme.breakpoints.mobileLandscape}) {
            position: sticky;
        }
    }

    &:nth-of-type(2) {
        @media (min-width: ${({ theme }) =>
                theme.breakpoints.mobileLandscape}) {
            position: sticky;
            z-index: 1;
            left: ${({ firstColWidth }) => `${firstColWidth}px`};
            border-right: ${({ theme }) => `1px solid ${theme.palette.black}`};
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        white-space: ${({ noWrapOnDesktop }) =>
            noWrapOnDesktop ? "nowrap" : "normal"};
    }
`;

export const ScreenReaderText = styled.h2`
    ${screenReaderText}
`;

const ProductTableMain = forwardRef(
    (
        {
            ariaLabel = "",
            fixed = false,
            smallTableHeaders = false,
            withPagination = false,
            useStickyScrollbar = false,
            rows = [],
            headerGroups,
            backgroundColor = "white",
            firstColumnStaticOnMobile = false,
            filterWrapperRef = {},
            paginationWrapperRef = {},
        },
        ref,
    ) => {
        const [firstColWidth, setFirstColWidth] = useState(0);

        const { descendingText, ascendingText } = useStringTranslation([
            { text: "Descending", handle: "descendingText" },
            { text: "Ascending", handle: "ascendingText" },
        ]);

        const windowScrollY = useRef(isBrowser ? window.scrollY : 0);
        const tableRef = ref;
        const tableHeaderRef = useRef();

        const { setHideImages } = useProductImageContext();

        const { totalHeight } = useStickyContext();

        const { filterHeight, paginationHeight } = useTableLayoutHelpers(
            filterWrapperRef,
            paginationWrapperRef,
        );

        useOnWindowResize(() => {
            setHideImages(true);
        }, false);

        useOnWindowResize(() => {
            const childNodes = tableHeaderRef?.current?.childNodes;
            const firstChild = find(childNodes, (node) => !!node);

            if (firstChild) {
                const colWidth = firstChild.getBoundingClientRect().width;
                setFirstColWidth(colWidth);
            }
        }, false);

        useEffect(() => {
            const setStickyScrollbar = (e) => {
                verticalScrollListener(
                    e,
                    windowScrollY,
                    tableRef,
                    tableHeaderRef,
                    totalHeight,
                );
            };

            if (rows.length > 5) {
                window.addEventListener("scroll", setStickyScrollbar, {
                    passive: true,
                });
            }

            return () => {
                window.removeEventListener("scroll", setStickyScrollbar);
            };
        }, [tableRef, rows, totalHeight]);

        const ariaLabelledBy = `${camelCase(ariaLabel)}HiddenLabel`;

        return (
            <ScrollSyncPane>
                <StyledTableWrap
                    stickyItemsHeight={totalHeight}
                    {...{
                        filterHeight,
                        paginationHeight,
                        useStickyScrollbar,
                        backgroundColor,
                    }}
                >
                    <ScreenReaderText id={ariaLabelledBy}>
                        {ariaLabel}
                    </ScreenReaderText>
                    <StyledTable
                        {...{ fixed }}
                        ref={tableRef}
                        aria-labelledby={ariaLabelledBy}
                    >
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr key={headerGroup.id} ref={tableHeaderRef}>
                                    {headerGroup.headers.map((header) => {
                                        const canSort =
                                            header.column.getCanSort();
                                        const isSorted =
                                            header.column.getIsSorted();

                                        let title;

                                        if (canSort) {
                                            if (
                                                header.column.getNextSortingOrder() ===
                                                "asc"
                                            ) {
                                                title = "Sort ascending";
                                            } else if (
                                                header.column.getNextSortingOrder() ===
                                                "desc"
                                            ) {
                                                title = "Sort descending";
                                            } else {
                                                title = "Clear sort";
                                            }
                                        }

                                        return (
                                            <ProductTableTh
                                                key={header.id}
                                                title={title}
                                                canBeSorted={canSort}
                                                smallTableHeaders={
                                                    smallTableHeaders
                                                }
                                                firstColWidth={firstColWidth}
                                                firstColumnStaticOnMobile={
                                                    firstColumnStaticOnMobile
                                                }
                                                onClick={header.column.getToggleSortingHandler()}
                                                aria-sort={
                                                    isSorted
                                                        ? isSorted === "desc"
                                                            ? "descending"
                                                            : "ascending"
                                                        : null
                                                }
                                            >
                                                <InnerTh>
                                                    <span>
                                                        {flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext(),
                                                        )}
                                                    </span>
                                                    {!canSort ||
                                                    rows.length < 2 ? null : (
                                                        <IconWrapper>
                                                            <ScreenReaderText aria-live="polite">
                                                                {`Sort by ${header.column.columnDef.header}${
                                                                    isSorted
                                                                        ? `: ${
                                                                              isSorted ===
                                                                              "desc"
                                                                                  ? descendingText
                                                                                  : ascendingText
                                                                          }`
                                                                        : ""
                                                                }`}
                                                            </ScreenReaderText>
                                                            {isSorted ? (
                                                                isSorted ===
                                                                "desc" ? (
                                                                    <SortIcon name="chevron-down" />
                                                                ) : (
                                                                    <SortIcon name="chevron-up" />
                                                                )
                                                            ) : (
                                                                <>
                                                                    <SortIcon name="chevron-up" />
                                                                    <SortIcon name="chevron-down" />
                                                                </>
                                                            )}
                                                        </IconWrapper>
                                                    )}
                                                </InnerTh>
                                            </ProductTableTh>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                return (
                                    <Tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <ProductTableTd
                                                key={cell.id}
                                                firstColWidth={firstColWidth}
                                                firstColumnStaticOnMobile={
                                                    firstColumnStaticOnMobile
                                                }
                                                noWrapOnDesktop={
                                                    cell.column.noWrapOnDesktop
                                                }
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </ProductTableTd>
                                        ))}
                                    </Tr>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                </StyledTableWrap>
            </ScrollSyncPane>
        );
    },
);

ProductTableMain.propTypes = {
    fixed: PropTypes.bool,
    smallTableHeaders: PropTypes.bool,
    withPagination: PropTypes.bool,
    ariaLabel: PropTypes.string,
    useStickyScrollbar: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    headerGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    backgroundColor: PropTypes.string,
    firstColumnStaticOnMobile: PropTypes.bool,
    filterWrapperRef: PropTypes.shape({}),
    paginationWrapperRef: PropTypes.shape({}),
};

export default ProductTableMain;
